import { z } from "zod";

// Compliance module form schemas.
//
// These mirror the backend's own rules so the officer sees a clear message
// before the round trip, not a 400 afterwards. The builder schema is also used
// live (on every keystroke) to drive the Save button's disabled state and the
// "what is still missing" tooltip, so its messages are written to be read by a
// user, one requirement per line.

const trimmed = (schema) => z.preprocess((v) => (typeof v === "string" ? v.trim() : v), schema);

// ── Name bounds and shape ────────────────────────────────────────────────────
//
// The same numbers and the same sentences as the server
// (server/app/api/v2/compliance/schema/api.py, `validate_display_name`), so the
// builder refuses exactly what a save would refuse. Python and JS share no
// constants channel, so neither derives from the other — keep the two in step
// by hand, and change both together.
export const CHECKLIST_NAME_MIN_LENGTH = 2;
export const CHECKLIST_NAME_MAX_LENGTH = 100;
export const SECTION_NAME_MIN_LENGTH = 2;
export const SECTION_NAME_MAX_LENGTH = 100;
export const DOCUMENT_NAME_MIN_LENGTH = 2;
export const DOCUMENT_NAME_MAX_LENGTH = 150;
export const FIELD_NAME_MIN_LENGTH = 2;
export const FIELD_NAME_MAX_LENGTH = 100;

// A name must CONTAIN a letter, not consist only of letters. Real document
// names carry digits and punctuation as a matter of course — the shipped
// country masters alone hold "Work Authorization (I-9 List A)", "Form W-4",
// "UAN / PF Details" and "Father's Name" — so the rule catches the names that
// say nothing ("12345", "!!!") and leaves the rest alone.
const HAS_LETTER = /\p{L}/u;

// Collapse inner runs as well as the ends: "UK  Documents" and "UK Documents"
// read identically, and the bounds are measured against the collapsed value —
// a name of nine spaces would otherwise clear a floor of 2.
const collapseWhitespace = (v) => (typeof v === "string" ? v.replace(/\s+/g, " ").trim() : v);

/**
 * One human-facing name: normalized, then checked for shape and bounds.
 *
 * `label` names the field in every message, because the builder shows four of
 * these at once and "Name must contain letters" does not say which one.
 */
const displayName = ({ label, min, max, requiredMessage }) =>
  z.preprocess(
    collapseWhitespace,
    z
      .string()
      .min(1, requiredMessage ?? `${label} is required`)
      .refine((v) => HAS_LETTER.test(v), {
        message: `${label} must contain letters and cannot be only numbers, spaces or special characters.`,
      })
      .refine((v) => v.length >= min, { message: `${label} must be at least ${min} characters.` })
      .refine((v) => v.length <= max, { message: `${label} cannot exceed ${max} characters.` }),
  );

// ── Tenant compliance settings (ComplianceSettingsCard) ──────────────────────

// Both numeric fields are held as strings in the card (an input must be
// clearable while typing), so coerce here rather than in the component.
const wholeNumberFromString = (min, max, label) =>
  z.preprocess(
    (v) => (v === "" || v === null || v === undefined ? NaN : Number(v)),
    z
      .number({ message: `${label} is required` })
      .int(`${label} must be a whole number`)
      .min(min, `${label} must be between ${min} and ${max}`)
      .max(max, `${label} must be between ${min} and ${max}`),
  );

export const complianceSettingsSchema = z.object({
  approvalThreshold: wholeNumberFromString(0, 100, "Approval threshold"),
  reviewSlaDays: wholeNumberFromString(0, 365, "Review SLA"),
  candidateStuckDays: wholeNumberFromString(0, 365, "Candidate stuck threshold"),
});

// ── Checklist builder tree ───────────────────────────────────────────────────

export const requiredFieldSchema = z.object({
  fieldName: displayName({
    label: "Field name",
    min: FIELD_NAME_MIN_LENGTH,
    max: FIELD_NAME_MAX_LENGTH,
    requiredMessage: "Every field needs a name",
  }),
  fieldType: z.string().min(1),
  isRequired: z.boolean().default(true),
});

export const subsectionSchema = z.object({
  name: displayName({
    label: "Document name",
    min: DOCUMENT_NAME_MIN_LENGTH,
    max: DOCUMENT_NAME_MAX_LENGTH,
    requiredMessage: "Every document needs a name",
  }),
  // At least 1, matching the server's `ge=1`. A zero-weight document is
  // collected, reviewed and approved without ever moving the candidate's
  // approval percentage — work that looks broken to the officer doing it and
  // to the candidate waiting on it.
  weightPercentage: z.preprocess(
    (v) => (v === "" || v === null || v === undefined ? NaN : Number(v)),
    z.number({ message: "Weight must be a number" }).min(1, "Weight must be at least 1%").max(100, "Weight cannot exceed 100"),
  ),
  // Named rather than bare ("must have at least 1 field"): these messages are
  // shown on their own in the Save tooltip, where a subjectless sentence gives
  // the officer no way to tell which document is at fault.
  requiredFields: z.array(requiredFieldSchema).min(1, "Each document needs at least 1 field"),
});

export const sectionSchema = z.object({
  name: displayName({
    label: "Section name",
    min: SECTION_NAME_MIN_LENGTH,
    max: SECTION_NAME_MAX_LENGTH,
    requiredMessage: "Every section needs a name",
  }),
  subsections: z.array(subsectionSchema).min(1, "Each section needs at least 1 document"),
});

// Used to gate "Add section": a new section is only useful once every
// existing one already has a name, a named document, and a named field.
// No array-level `.min()` here — an empty list (nothing added yet) must
// still let the very first section through.
export const sectionsListSchema = z.array(sectionSchema);

// Used to gate "Add document": a new document within a section is only
// useful once every existing document in that section already has a name
// and at least one named field.
export const subsectionsListSchema = z.array(subsectionSchema);

// Used to gate "Add field": a new field on a document is only useful once
// every existing field on that document already has a name.
export const requiredFieldsListSchema = z.array(requiredFieldSchema);

/**
 * The builder's shape check, in one of two weight modes (ATH-I1765).
 *
 * `isCountryMaster: false` (default) — an assignable checklist. Weights total
 * exactly 100, because the backend derives weighted approval progress from that
 * total and any other sum makes every candidate's percentage meaningless.
 *
 * `isCountryMaster: true` — a country document set. Totals 1..100: it is the
 * source a checklist is built from, and the leftover room is what the officer
 * spends on the documents that define their domain. The server applies the same
 * split (`ChecklistWeightMode`), and a builder that disagreed would refuse to
 * save what the server accepts — which is exactly the bug this replaced.
 */
export const buildChecklistBuilderSchema = ({ isCountryMaster = false } = {}) =>
  z
    .object({
      name: displayName({
        label: "Checklist name",
        min: CHECKLIST_NAME_MIN_LENGTH,
        max: CHECKLIST_NAME_MAX_LENGTH,
        requiredMessage: "Please enter the checklist name",
      }),
      // Required: the region decides which checklist applies to a candidate and
      // backs the region filters, so a checklist without one is unreachable by
      // both. Chosen from the country dropdown, not typed.
      region: trimmed(z.string({ message: "Please select a country" }).min(1, "Please select a country").max(100, "Region is too long")),
      sections: z.array(sectionSchema).min(1, "Add at least one section"),
    })
    .superRefine((value, ctx) => {
      const total = value.sections.flatMap((sec) => sec.subsections ?? []).reduce((sum, sub) => sum + (Number(sub.weightPercentage) || 0), 0);

      const valid = isCountryMaster ? total >= 1 && total <= 100 : total === 100;
      if (!valid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sections"],
          message: isCountryMaster
            ? `Document weights must total between 1% and 100% (currently ${total}%)`
            : `Document weights must total exactly 100% (currently ${total}%)`,
        });
      }
    });

/** The assignable-checklist rule — the default, and what every existing caller
 * of this schema means. */
export const checklistBuilderSchema = buildChecklistBuilderSchema();

// ── Reject / re-request reason (detail page) ─────────────────────────────────

// A re-request is an instruction the candidate acts on, so it must say
// something; a rejection reason is optional (the status alone is the decision).
export const documentReasonSchema = z
  .object({
    mode: z.enum(["reject", "rerequest"]),
    text: trimmed(z.string().max(1000, "Reason cannot exceed 1000 characters").default("")),
  })
  .superRefine((value, ctx) => {
    if (value.mode === "rerequest" && !value.text) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["text"],
        message: "Please describe what the candidate needs to fix.",
      });
    }
  });

// ── Extracted-field confirmation (detail page) ───────────────────────────────

// Per-`fieldType` shape checks (mirrors REQUIRED_FIELD_TYPES in
// pages/admin/compliance/constant.js / the backend's
// ALLOWED_REQUIRED_FIELD_TYPES). Every extracted value arrives as a string —
// from an <Input>/<Textarea>, not a typed control — so these check the string
// *looks like* that type rather than coercing it. Types with no entry here
// (text, select, …) get no extra shape check beyond required/optional.
const FIELD_TYPE_CHECKS = {
  // Space-grouped digits (Aadhaar "4444 3333 2222", card/account numbers,
  // MICR codes, …) are the normal shape for this field type, not free text —
  // strip the spaces before checking rather than rejecting a well-formed
  // extracted value. Number("4444 3333 2222") is NaN even though the digits
  // are fine, since Number() only trims leading/trailing whitespace.
  number: (v) => {
    const digits = v.replace(/\s+/g, "");
    return digits !== "" && !Number.isNaN(Number(digits));
  },
  // Strict ISO 8601 (YYYY-MM-DD) — the shape the extraction prompts ask the
  // AI for (see generatedPrompt on each subsection). Date.parse() alone is
  // too permissive: it accepts locale-ambiguous formats ("08/27/2026" vs
  // "27/08/2026") and free text ("Aug 27, 2026"), and doesn't catch a
  // calendar-invalid date (Date.parse("2026-02-30") silently rolls over to
  // March). Round-tripping through Date.UTC and comparing the components back
  // catches both.
  date: (v) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    if (!m) return false;
    const [, y, mo, d] = m.map(Number);
    const parsed = new Date(Date.UTC(y, mo - 1, d));
    return parsed.getUTCFullYear() === y && parsed.getUTCMonth() === mo - 1 && parsed.getUTCDate() === d;
  },
  // Strictly "true"/"false" (case-insensitive) — not "yes"/"no"/"1"/"0". The
  // confirmed value is stored and displayed verbatim, so accepting synonyms
  // here would let a document end up with an unnormalized value like "yes"
  // where every other boolean field reads "true".
  boolean: (v) => ["true", "false"].includes(v.toLowerCase()),
  email: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
  phone: (v) => /^[+]?[\d\s()-]{7,15}$/.test(v),
};

const FIELD_TYPE_MESSAGES = {
  number: (label) => `${label} must be a valid number`,
  date: (label) => `${label} must be a valid date (YYYY-MM-DD)`,
  boolean: (label) => `${label} must be "true" or "false"`,
  email: (label) => `${label} must be a valid email address`,
  phone: (label) => `${label} must be a valid phone number`,
};

// Built per-document from that document's checklist `requiredFields`, since
// the set of fields (which are required, and what type each is) differs per
// subsection. Optional fields may stay blank; required ones must have a
// non-empty value before an officer can save/approve — an AI extraction the
// officer never verified is worse than no value at all. Any value that IS
// present, required or not, is checked against its configured fieldType, so
// e.g. an AI-mangled "Aadhaar Number" of "abcd" can't be saved as-is.
export const buildFieldValuesSchema = (requiredFields) => {
  const shape = {};
  (requiredFields ?? []).forEach((f) => {
    const label = f.fieldName;
    const isRequired = f.isRequired !== false;
    const typeCheck = FIELD_TYPE_CHECKS[f.fieldType];

    shape[f.fieldName] = trimmed(z.string().default("")).superRefine((value, ctx) => {
      if (value === "") {
        if (isRequired) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${label} is required` });
        }
        return;
      }
      if (typeCheck && !typeCheck(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: FIELD_TYPE_MESSAGES[f.fieldType](label),
        });
      }
    });
  });
  return z.object(shape);
};

/**
 * Flatten a Zod error into a plain, user-readable message list. Used by the
 * builder's requirements tooltip, which shows every outstanding rule at once
 * rather than only the first failure.
 */
export const zodErrorMessages = (error) => (error?.issues ?? []).map((issue) => issue.message).filter(Boolean);

/**
 * Same flattening, keyed by the top-level field name instead of a flat list —
 * used to show each error next to the input it belongs to (extracted-field
 * confirmation) rather than as a single combined message.
 */
export const zodFieldErrors = (error) => {
  const errors = {};
  (error?.issues ?? []).forEach((issue) => {
    const field = issue.path?.[0];
    if (field && !errors[field]) errors[field] = issue.message;
  });
  return errors;
};
